import * as React from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'next-i18next';

import { Title, JobPositionCard, JobPositionCardProps, Button } from '@restworld/ui-ds';
import { useAnalytics, useResponsiveBreakPoints } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

export interface IJobPositionsOfStaticOccupationProps {
  staticOccupations: {
    id: string;
    occupation: string;
    jobs: JobPositionCardProps[];
  }[];
  testId?: string;
}

export const JobPositionsOfStaticOccupation: React.FunctionComponent<IJobPositionsOfStaticOccupationProps> = ({
  staticOccupations,
  testId,
}) => {
  const [activeOccupation, setActiveOccupation] = React.useState<string | null>(null);
  const [occupationJobs, setOccupationJobs] = React.useState<JobPositionCardProps[]>([]);
  const [showMore, setShowMore] = React.useState<boolean>(false);
  const { isDesktop } = useResponsiveBreakPoints();
  const { t: commonTranslate } = useTranslation('common');
  const { t: homePageTranslate } = useTranslation('home-page');
  const auth = useAuth();
  const { trackEvent } = useAnalytics();
  const recordViewJobPositionEvent = React.useCallback(
    (jobProps: JobPositionCardProps) => {
      trackEvent('view_job', {
        email: auth?.user?.email,
        role: auth?.user?.type,
        origin: 'home_page',
        id: jobProps?.id,
        job_title: jobProps?.jobTitle,
        occupation: jobProps?.occupation,
        salary_min: jobProps?.salaryMin,
        salary_max: jobProps?.salaryMax,
        job_type: jobProps?.jobType,
        published_at: jobProps?.publishedAt,
        restaurant_name: jobProps?.restaurantName,
        city: jobProps?.city,
      });
    },
    [auth?.user?.email, auth?.user?.type, trackEvent]
  );

  function OnChangeOccupation(tab: string) {
    setActiveOccupation(tab);
  }

  React.useEffect(() => {
    if (!activeOccupation) {
      if (isDesktop) {
        setOccupationJobs(staticOccupations[0].jobs);
      } else {
        if (showMore) {
          setOccupationJobs(staticOccupations[0].jobs);
        } else {
          setOccupationJobs(staticOccupations[0].jobs.slice(0, 3));
        }
      }
      setActiveOccupation(staticOccupations[0].id);
    } else {
      const selectedOccupation = staticOccupations.find((v) => v.id === activeOccupation);
      if (selectedOccupation) {
        if (isDesktop) {
          setOccupationJobs(selectedOccupation.jobs);
        } else {
          if (showMore) {
            setOccupationJobs(selectedOccupation.jobs);
          } else {
            setOccupationJobs(selectedOccupation.jobs.slice(0, 3));
          }
        }
      }
    }
  }, [activeOccupation, isDesktop, showMore, staticOccupations]);

  return (
    <div tw="flex flex-col gap-8 pb-15" data-cy={testId}>
      <div tw="flex flex-col gap-6">
        <Title
          twStyle={tw`leading-11.5`}
          fontSize="superSuperLarge"
          fontWeight="bold"
          content={homePageTranslate('explore_career_opportunities')}
          testId="job-positions-of-static-occupation-title"
        />
        <div
          data-cy="static-occupations"
          tw="flex flex-row gap-4 box-border w-full justify-items-start p-1 overflow-x-auto scrollbar-none"
        >
          {staticOccupations.map(({ id, occupation }, index) => (
            <Button
              testId={`static-occupations-${index}`}
              variant={id === activeOccupation ? 'primary' : 'gray-outline'}
              onClick={() => OnChangeOccupation(id)}
              key={index}
            >
              {occupation}
            </Button>
          ))}
        </div>
      </div>
      <ul data-cy="occupation-jobs" tw="grid xl:grid-cols-3 lg:grid-cols-2 grid-cols-1 gap-6">
        {occupationJobs.map((v, i) => (
          <li key={i}>
            <JobPositionCard
              {...v}
              onLinkClick={() => {
                recordViewJobPositionEvent(v);
              }}
              testId={`occupations-jobs-${i}`}
            />
          </li>
        ))}
        {!isDesktop && (
          <Button onClick={() => setShowMore((prev) => !prev)} variant="secondary-outline" fullwidth={true}>
            {!showMore ? commonTranslate('show_more') : commonTranslate('show_less')}
          </Button>
        )}
      </ul>
    </div>
  );
};

export default JobPositionsOfStaticOccupation;
